import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Icon, IconButton } from '@openedx/paragon';
import { RiStackLine } from '@remixicon/react';
import { useIntl } from '@edx/frontend-platform/i18n';
import { logError } from '@edx/frontend-platform/logging';
import { useFetchAllFlashcards, useSubmitFlashcardReview } from '../../../gql';

import FlashcardItem from './FlashcardItem';
import messages from './messages';

// Must stay in sync with the .flashcard-item--dismissing animation duration.
const DISMISS_ANIMATION_MS = 380;
// How long a card's result stays on screen after the attempt, before the exit
// animation starts. Each card type asks for its own hold (a quiz verdict reads
// faster than a text card's back), so this is only the fallback.
const DEFAULT_HOLD_MS = 3000;
const NAV_VISIBLE_THRESHOLD = 4;

// FSRS rating scale. Attempting a card records one of these against the learner's
// memory state; the card resurfaces only when it next falls due.
const FSRS_RATING = {
  AGAIN: 1, HARD: 2, GOOD: 3, EASY: 4,
};

// A card is due when the learner has no review row yet (never studied — resurface
// immediately) or its scheduled `due` time has passed.
const isDue = (due, now) => !due || new Date(due).getTime() <= now;

const ArrowLeftWideFill = (props) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <svg className="flashcards-section__navIcon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M5.57898 8.00007L8.74411 2.02148L9.92251 2.64534L7.08765 8.00007L9.92251 13.3548L8.74411 13.9787L5.57898 8.00007Z" fill="currentColor" />
  </svg>
);

const ArrowRightWideFill = (props) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <svg className="flashcards-section__navIcon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M10.421 8.00007L7.2559 2.02148L6.07751 2.64534L8.91237 8.00007L6.07751 13.3548L7.2559 13.9787L10.421 8.00007Z" fill="currentColor" />
  </svg>
);

const FlashcardsSection = () => {
  const intl = useIntl();
  const { flashcards, loading, error } = useFetchAllFlashcards();
  const submitReview = useSubmitFlashcardReview();

  const scrollRef = useRef(null);
  // Cards mid-exit-animation, and cards already gone. Both are sets because
  // several cards can be in flight at once — a learner can answer a second card
  // while the first is still showing its result.
  const [exitingIds, setExitingIds] = useState(() => new Set());
  const [dismissedIds, setDismissedIds] = useState(() => new Set());
  // Cards whose review has already been submitted, so a stray second attempt
  // can't double-post. A ref, not state: it must be readable synchronously.
  const attemptedRef = useRef(new Set());
  const timersRef = useRef([]);

  useEffect(() => () => { timersRef.current.forEach(clearTimeout); }, []);

  const now = Date.now();
  const items = (flashcards || [])
    .map((card) => {
      const flashcardUser = card?.flashcardusers?.[0];
      const review = card?.flashcardReviews?.[0];
      return {
        id: card?.id,
        cardType: card?.cardType,
        flashcardUserId: flashcardUser?.id,
        enrolled: !!flashcardUser,
        due: review?.due ?? null,
        ...(card?.cardData || {}),
      };
    })
    // Show only cards the learner is enrolled in that are due now, minus any the
    // learner just dismissed locally (so the slot clears before the round-trip).
    .filter((item) => (
      !!item.id
      && item.enrolled
      && isDue(item.due, now)
      && !dismissedIds.has(item.id)
    ));

  /**
   * The attempt IS the review: answering a quiz or flipping a text card records
   * it immediately, then the card lingers on its result for `holdMs` before
   * animating away. There is no confirm step — nothing is left for the learner
   * to press, so no attempt can be lost by walking away from the card.
   *
   * `id` is the flashcard id — exactly what the FSRS review endpoint expects.
   */
  const handleAttempt = useCallback((id, rating = FSRS_RATING.GOOD, holdMs = DEFAULT_HOLD_MS) => {
    if (!id || attemptedRef.current.has(id)) { return; }
    attemptedRef.current.add(id);

    // Submitted up front, not on the way out: a reload or navigation during the
    // hold must not cost the learner their review. Failures are logged — the
    // card resurfaces as due on the next load, so nothing is silently accepted.
    Promise.resolve(submitReview(id, rating)).catch(logError);

    const holdTimer = setTimeout(() => {
      setExitingIds((prev) => new Set(prev).add(id));
      const exitTimer = setTimeout(() => {
        setDismissedIds((prev) => new Set(prev).add(id));
        setExitingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, DISMISS_ANIMATION_MS);
      timersRef.current.push(exitTimer);
    }, holdMs);
    timersRef.current.push(holdTimer);
  }, [submitReview]);

  const scrollByOneCard = (direction) => {
    const el = scrollRef.current;
    if (!el) { return; }
    const card = el.querySelector('.flashcard-item');
    const step = (card?.offsetWidth ?? 269) + 24;
    el.scrollBy({ left: direction * step, behavior: 'smooth' });
  };

  if (loading || error || items.length === 0) {
    return null;
  }

  const showNav = items.length >= NAV_VISIBLE_THRESHOLD;

  return (
    <section className="flashcards-section">
      <div className="flashcards-section__header">
        <div className="flashcards-section__headerText">
          <div className="flashcards-section__headerRow">
            <Icon className="flashcards-section__headerIcon" src={RiStackLine} size="sm" />
            <div className="flashcards-section__title">{intl.formatMessage(messages.flashcards)}</div>
          </div>
          <div className="flashcards-section__subtitleRow">
            {/* <Icon className="flashcards-section__subtitleIcon" src={RiStackLine} size="sm" /> */}
            <div className="flashcards-section__subtitle">{intl.formatMessage(messages.flashcardsSubtitle)}</div>
          </div>
        </div>
        {showNav && (
          <div className="flashcards-section__nav">
            <IconButton
              className="flashcards-section__navBtn"
              alt={intl.formatMessage(messages.flashcardsScrollPrev)}
              src={ArrowLeftWideFill}
              variant="tertiary"
              size="sm"
              onClick={() => scrollByOneCard(-1)}
            />
            <IconButton
              className="flashcards-section__navBtn"
              alt={intl.formatMessage(messages.flashcardsScrollNext)}
              src={ArrowRightWideFill}
              variant="tertiary"
              size="sm"
              onClick={() => scrollByOneCard(1)}
            />
          </div>
        )}
      </div>

      <div className="flashcards-section__scrollWrapper">
        <div
          ref={scrollRef}
          className="flashcards-section__scroll"
          aria-label={intl.formatMessage(messages.flashcardsListLabel)}
        >
          {items.map((item) => {
            const { id } = item;
            // Quiz cards pass an explicit rating (correct → Good, wrong → Again)
            // plus how long their verdict should stay up; text cards do the same
            // for their back face.
            const onAttempt = (rating, holdMs) => handleAttempt(id, rating, holdMs);
            return (
              <FlashcardItem
                // eslint-disable-next-line react/jsx-props-no-spreading
                key={id}
                {...item}
                onAttempt={onAttempt}
                isDismissing={exitingIds.has(id)}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FlashcardsSection;
